import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAdmin } from '../../components/Admin/AdminContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { 
  Clock, CheckCircle, XCircle, AlertCircle, MapPin, User,
  Calendar, Heart, MessageCircle, Image as ImageIcon, Eye,
  RefreshCw, ChevronDown, Shield, Star, Loader2
} from 'lucide-react';

export default function AdminModeration() {
  const { adminAPI } = useAdmin();
  const [spots, setSpots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [stats, setStats] = useState(null);
  const [selectedSpot, setSelectedSpot] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [approvalNotes, setApprovalNotes] = useState('');
  const [showRejectModal, setShowRejectModal] = useState(false);
  
  const observerRef = useRef();
  const loadMoreRef = useRef();

  useEffect(() => {
    loadInitialData();
  }, []);

  // Intersection Observer pour scroll infini
  useEffect(() => {
    const options = {
      root: null,
      rootMargin: '100px',
      threshold: 0.1
    };

    observerRef.current = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting && hasMore && !loadingMore && !loading) {
        loadMoreSpots();
      }
    }, options);

    if (loadMoreRef.current) {
      observerRef.current.observe(loadMoreRef.current);
    }

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [hasMore, loadingMore, loading, spots.length]);

  const loadInitialData = async () => {
    setLoading(true);
    try {
      const [spotsData, statsData] = await Promise.all([
        adminAPI.getModerationPending({ skip: 0, limit: 20 }),
        adminAPI.getModerationStats()
      ]);
      
      setSpots(spotsData.spots || []);
      setHasMore((spotsData.spots || []).length === 20);
      setStats(statsData);
    } catch (error) {
      console.error('Failed to load moderation data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadMoreSpots = async () => {
    if (loadingMore || !hasMore) return;
    
    setLoadingMore(true);
    try {
      const data = await adminAPI.getModerationPending({ 
        skip: spots.length, 
        limit: 20 
      });
      
      const newSpots = data.spots || [];
      setSpots(prev => [...prev, ...newSpots]);
      setHasMore(newSpots.length === 20);
    } catch (error) {
      console.error('Failed to load more spots:', error);
    } finally {
      setLoadingMore(false);
    }
  };

  const handleApprove = async (spotId) => {
    try {
      setActionLoading(true);
      await adminAPI.approveSpot(spotId, approvalNotes || null);
      setSpots(prev => prev.filter(s => s.id !== spotId));
      setSelectedSpot(null);
      setApprovalNotes('');
      loadStats();
    } catch (error) {
      console.error('Failed to approve spot:', error);
      alert('Erreur lors de l\'approbation');
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async (spotId) => {
    if (!rejectReason.trim()) {
      alert('Veuillez indiquer une raison de rejet');
      return;
    }
    
    try {
      setActionLoading(true);
      await adminAPI.rejectSpot(spotId, rejectReason);
      setSpots(prev => prev.filter(s => s.id !== spotId));
      setSelectedSpot(null);
      setShowRejectModal(false);
      setRejectReason('');
      loadStats();
    } catch (error) {
      console.error('Failed to reject spot:', error);
      alert('Erreur lors du rejet');
    } finally {
      setActionLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const data = await adminAPI.getModerationStats();
      setStats(data);
    } catch (error) {
      console.error('Failed to load stats:', error);
    }
  };

  const getTrustBadge = (trustLevel, role) => {
    if (role === 'admin') return <Badge className="bg-purple-100 text-purple-700"><Shield className="h-3 w-3 mr-1" />Admin</Badge>;
    if (role === 'moderator') return <Badge className="bg-blue-100 text-blue-700"><Shield className="h-3 w-3 mr-1" />Modérateur</Badge>;
    if (role === 'trusted') return <Badge className="bg-green-100 text-green-700"><Star className="h-3 w-3 mr-1" />Confiance</Badge>;
    return <Badge className="bg-gray-100 text-gray-700"><User className="h-3 w-3 mr-1" />Nouveau</Badge>;
  };

  // Modal de détail du spot
  const SpotDetailModal = ({ spot }) => (
    <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2">
          <Clock className="h-5 w-5 text-yellow-600" />
          Modération: {spot.title}
        </DialogTitle>
        <DialogDescription>
          Examinez les détails et décidez d'approuver ou rejeter ce spot
        </DialogDescription>
      </DialogHeader>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Photos */}
        <div>
          <Label className="text-sm text-gray-500">Photos ({spot.photos?.length || 0})</Label>
          <div className="grid grid-cols-2 gap-2 mt-2">
            {spot.photos?.slice(0, 4).map((photo, i) => (
              <img 
                key={i}
                src={photo.url || photo}
                alt={`Photo ${i + 1}`}
                className="w-full h-32 object-cover rounded-lg"
              />
            ))}
          </div>
        </div>

        {/* Infos */}
        <div className="space-y-4">
          <div>
            <Label className="text-sm text-gray-500">Auteur</Label>
            <div className="flex items-center gap-2 mt-1">
              <span className="font-medium">{spot.author}</span>
              {spot.author_info && getTrustBadge(spot.author_info.trust_level, spot.author_info.role)}
            </div>
            {spot.author_info && (
              <p className="text-xs text-gray-500 mt-1">
                {spot.author_info.approved_spots_count || 0} spots approuvés • 
                {spot.author_info.rejected_spots_count || 0} rejetés
              </p>
            )}
          </div>

          <div>
            <Label className="text-sm text-gray-500">Description</Label>
            <p className="mt-1 text-sm">{spot.description}</p>
          </div>

          <div>
            <Label className="text-sm text-gray-500">Tags</Label>
            <div className="flex flex-wrap gap-1 mt-1">
              {spot.tags?.map((tag, i) => (
                <Badge key={i} variant="secondary" className="text-xs">#{tag}</Badge>
              ))}
            </div>
          </div>

          <div>
            <Label className="text-sm text-gray-500">Coordonnées</Label>
            <p className="text-sm mt-1">
              {spot.latitude?.toFixed(4)}, {spot.longitude?.toFixed(4)}
            </p>
          </div>

          <div>
            <Label className="text-sm text-gray-500">Date de soumission</Label>
            <p className="text-sm mt-1">
              {new Date(spot.created_at).toLocaleString('fr-FR')}
            </p>
          </div>
        </div>
      </div>

      {/* Notes d'approbation */}
      <div className="mt-4">
        <Label>Notes de modération (optionnel)</Label>
        <Textarea
          value={approvalNotes}
          onChange={(e) => setApprovalNotes(e.target.value)}
          placeholder="Notes internes sur cette décision..."
          rows={2}
          className="mt-1"
        />
      </div>

      {/* Actions */}
      <div className="flex gap-3 mt-6">
        <Button 
          onClick={() => handleApprove(spot.id)}
          disabled={actionLoading}
          className="flex-1 bg-green-600 hover:bg-green-700"
        >
          {actionLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <CheckCircle className="h-4 w-4 mr-2" />}
          Approuver
        </Button>
        <Button 
          onClick={() => setShowRejectModal(true)}
          disabled={actionLoading}
          variant="destructive"
          className="flex-1"
        >
          <XCircle className="h-4 w-4 mr-2" />
          Rejeter
        </Button>
        <Button 
          onClick={() => setSelectedSpot(null)}
          variant="outline"
        >
          Fermer
        </Button>
      </div>
    </DialogContent>
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Modération des Spots</h1>
          <p className="text-gray-600 mt-1">Validez ou rejetez les nouveaux spots soumis</p>
        </div>
        <Button variant="outline" onClick={loadInitialData}>
          <RefreshCw className="h-4 w-4 mr-2" />
          Actualiser
        </Button>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <Card className="border-2 border-yellow-200 bg-yellow-50">
            <CardContent className="p-4 text-center">
              <Clock className="h-6 w-6 mx-auto text-yellow-600 mb-2" />
              <p className="text-2xl font-bold text-yellow-700">{stats.pending}</p>
              <p className="text-sm text-yellow-600">En attente</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <CheckCircle className="h-6 w-6 mx-auto text-green-500 mb-2" />
              <p className="text-2xl font-bold text-green-600">{stats.approved}</p>
              <p className="text-sm text-gray-500">Approuvés</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <XCircle className="h-6 w-6 mx-auto text-red-500 mb-2" />
              <p className="text-2xl font-bold text-red-600">{stats.rejected}</p>
              <p className="text-sm text-gray-500">Rejetés</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <CheckCircle className="h-6 w-6 mx-auto text-green-400 mb-2" />
              <p className="text-2xl font-bold">{stats.approved_this_week}</p>
              <p className="text-sm text-gray-500">Cette semaine ✓</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <XCircle className="h-6 w-6 mx-auto text-red-400 mb-2" />
              <p className="text-2xl font-bold">{stats.rejected_this_week}</p>
              <p className="text-sm text-gray-500">Cette semaine ✗</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Liste des spots en attente */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <AlertCircle className="h-5 w-5 text-yellow-600" />
            Spots en attente de validation
          </CardTitle>
          <CardDescription>
            Cliquez sur un spot pour voir les détails et le modérer
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
            </div>
          ) : spots.length === 0 ? (
            <div className="text-center py-12">
              <CheckCircle className="h-16 w-16 text-green-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900">Aucun spot en attente</h3>
              <p className="text-gray-500">Tous les spots ont été modérés !</p>
            </div>
          ) : (
            <div className="space-y-4">
              {spots.map((spot) => (
                <div 
                  key={spot.id}
                  onClick={() => setSelectedSpot(spot)}
                  className="flex items-center gap-4 p-4 border border-yellow-200 bg-yellow-50/50 rounded-lg hover:bg-yellow-100/50 cursor-pointer transition-colors"
                >
                  {/* Miniature */}
                  <div className="w-20 h-20 flex-shrink-0">
                    {spot.photos?.[0] ? (
                      <img 
                        src={spot.photos[0].url || spot.photos[0]}
                        alt=""
                        className="w-full h-full object-cover rounded-lg"
                      />
                    ) : (
                      <div className="w-full h-full bg-gray-200 rounded-lg flex items-center justify-center">
                        <MapPin className="h-8 w-8 text-gray-400" />
                      </div>
                    )}
                  </div>

                  {/* Infos */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-medium text-gray-900 truncate">{spot.title}</h3>
                      <Badge className="bg-yellow-100 text-yellow-700">
                        <Clock className="h-3 w-3 mr-1" />
                        En attente
                      </Badge>
                    </div>
                    
                    <p className="text-sm text-gray-600 truncate mb-2">{spot.description}</p>
                    
                    <div className="flex items-center gap-4 text-xs text-gray-500">
                      <span className="flex items-center gap-1">
                        <User className="h-3 w-3" />
                        {spot.author}
                        {spot.author_info && (
                          <span className="ml-1">
                            {getTrustBadge(spot.author_info.trust_level, spot.author_info.role)}
                          </span>
                        )}
                      </span>
                      <span className="flex items-center gap-1">
                        <Calendar className="h-3 w-3" />
                        {new Date(spot.created_at).toLocaleDateString('fr-FR')}
                      </span>
                      <span className="flex items-center gap-1">
                        <ImageIcon className="h-3 w-3" />
                        {spot.photos?.length || 0} photos
                      </span>
                    </div>
                  </div>

                  {/* Actions rapides */}
                  <div className="flex gap-2">
                    <Button 
                      size="sm" 
                      className="bg-green-600 hover:bg-green-700"
                      onClick={(e) => { e.stopPropagation(); handleApprove(spot.id); }}
                    >
                      <CheckCircle className="h-4 w-4" />
                    </Button>
                    <Button 
                      size="sm" 
                      variant="destructive"
                      onClick={(e) => { e.stopPropagation(); setSelectedSpot(spot); setShowRejectModal(true); }}
                    >
                      <XCircle className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}

              {/* Loader pour scroll infini */}
              <div ref={loadMoreRef} className="flex justify-center py-4">
                {loadingMore && (
                  <div className="flex items-center gap-2 text-gray-500">
                    <Loader2 className="h-5 w-5 animate-spin" />
                    <span>Chargement...</span>
                  </div>
                )}
                {!hasMore && spots.length > 0 && (
                  <p className="text-gray-400 text-sm">Fin de la liste</p>
                )}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Modal de détail */}
      <Dialog open={!!selectedSpot && !showRejectModal} onOpenChange={() => setSelectedSpot(null)}>
        {selectedSpot && <SpotDetailModal spot={selectedSpot} />}
      </Dialog>

      {/* Modal de rejet */}
      <Dialog open={showRejectModal} onOpenChange={setShowRejectModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <XCircle className="h-5 w-5" />
              Rejeter ce spot
            </DialogTitle>
            <DialogDescription>
              Indiquez la raison du rejet (sera visible par l'utilisateur)
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label>Raison du rejet *</Label>
              <Textarea
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="Ex: Photos de mauvaise qualité, description insuffisante, doublon..."
                rows={3}
                className="mt-1"
              />
            </div>

            <div className="flex gap-2">
              <Button 
                onClick={() => selectedSpot && handleReject(selectedSpot.id)}
                disabled={actionLoading || !rejectReason.trim()}
                variant="destructive"
                className="flex-1"
              >
                {actionLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                Confirmer le rejet
              </Button>
              <Button variant="outline" onClick={() => setShowRejectModal(false)}>
                Annuler
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
